/* Video-reference navigation. Shopify's own search, account and cart components remain authoritative. */
class RSHeader extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    const options = { signal: this.controller.signal };
    this.bar = this.querySelector('.rs-header__bar');
    this.drawer = this.querySelector('[data-rs-menu]');
    this.primaryPane = this.querySelector('[data-rs-primary-pane]');
    this.childrenPane = this.querySelector('[data-rs-children-pane]');
    this.trigger = this.querySelector('[data-rs-menu-open]');
    this.backdrop = this.querySelector('[data-rs-nav-backdrop]');
    this.rail = this.querySelector('[data-rs-inspiration-rail]');
    this.mobile = window.matchMedia('(max-width: 749px)');
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.menuOpen = false;
    this.activeBranch = null;
    this.inertElements = [];

    this.trigger?.addEventListener('click', () => this.menuOpen ? this.closeMenu() : this.openMenu(), options);
    this.backdrop?.addEventListener('click', () => {
      this.closeMenu();
      this.closeMegas();
    }, options);
    this.querySelectorAll('[data-rs-branch]').forEach((button) => {
      button.addEventListener('click', () => {
        // Pointer entry may already have selected this branch. A following click
        // must not undo that selection before the customer can use its links.
        this.selectBranch(button, this.mobile.matches);
      }, options);
      button.addEventListener('pointerenter', (event) => {
        if (event.pointerType === 'mouse' && !this.mobile.matches && this.menuOpen) this.selectBranch(button);
      }, options);
      button.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.selectBranch(button, true);
        }
      }, options);
    });
    this.querySelectorAll('[data-rs-branch-back]').forEach((button) => {
      button.addEventListener('click', () => this.closeBranch(true), options);
    });
    this.drawer?.addEventListener('click', (event) => {
      if (event.target.closest('a[href]')) this.closeMenu(false);
    }, options);
    // Close our overlay before a native top-layer search/account/cart dialog takes focus.
    this.querySelector('.rs-header__actions')?.addEventListener('click', (event) => {
      if (!event.target.closest('.rs-localization')) {
        this.closeMenu(false);
        this.closeMegas();
      }
    }, { ...options, capture: true });
    this.querySelector('.rs-header__brand')?.addEventListener('click', () => this.closeMenu(false), options);

    this.querySelectorAll('.rs-mega').forEach((details) => {
      const summary = details.querySelector('summary');
      const open = () => {
        clearTimeout(this.closeTimer);
        this.closeMenu(false);
        this.closeMegas(details);
        details.open = true;
        this.updateOpenState();
      };
      details.addEventListener('pointerenter', (event) => {
        if (event.pointerType === 'mouse') open();
      }, options);
      details.addEventListener('pointerleave', (event) => {
        if (event.pointerType !== 'mouse') return;
        this.closeTimer = setTimeout(() => {
          if (!details.contains(document.activeElement)) {
            details.open = false;
            this.updateOpenState();
          }
        }, 220);
      }, options);
      details.addEventListener('toggle', () => {
        if (details.open) {
          this.closeMenu(false);
          this.closeMegas(details);
        }
        this.updateOpenState();
      }, options);
      summary?.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowDown') return;
        event.preventDefault();
        open();
        this.focusable(details.querySelector('.rs-mega__panel'))[0]?.focus();
      }, options);
      summary?.addEventListener('click', (event) => {
        event.preventDefault();
        // A mouse hover opens the menu before click; keep that menu open.
        // Keyboard-generated clicks retain the familiar toggle behavior.
        if (details.open && event.detail === 0) {
          details.open = false;
          this.updateOpenState();
        } else open();
      }, options);
      details.addEventListener('focusout', () => {
        queueMicrotask(() => {
          if (!details.contains(document.activeElement) && !details.matches(':hover')) {
            details.open = false;
            this.updateOpenState();
          }
        });
      }, options);
    });
    this.querySelectorAll('.rs-localization').forEach((details) => {
      details.addEventListener('toggle', () => {
        if (details.open) {
          this.querySelectorAll('.rs-localization[open]').forEach((other) => { if (other !== details) other.open = false; });
          this.closeMegas();
        }
        this.updateOpenState();
      }, options);
    });
    document.addEventListener('click', (event) => {
      if (!this.contains(event.target)) {
        this.closeMegas();
        this.querySelectorAll('.rs-localization[open]').forEach((details) => details.open = false);
      }
    }, options);
    this.addEventListener('keydown', (event) => this.onKeydown(event), options);
    this.querySelector('[data-rs-rail-prev]')?.addEventListener('click', () => this.scrollRail(-1), options);
    this.querySelector('[data-rs-rail-next]')?.addEventListener('click', () => this.scrollRail(1), options);
    this.rail?.addEventListener('scroll', () => { this.updateRail(); this.syncVideos(); }, { ...options, passive: true });
    this.rail?.addEventListener('keydown', (event) => {
      if (event.target !== this.rail || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
      event.preventDefault();
      this.scrollRail(event.key === 'ArrowRight' ? 1 : -1);
    }, options);
    this.mobile.addEventListener('change', () => {
      this.syncPrimaryInert();
      if (this.mobile.matches) this.closeMegas();
      if (this.menuOpen && this.activeBranch) {
        if (this.mobile.matches) this.focusable(this.activeBranch)[0]?.focus();
        else this.querySelector(`[aria-controls="${CSS.escape(this.activeBranch.id)}"]`)?.focus();
      }
      this.update();
    }, options);
    this.reducedMotion.addEventListener('change', () => this.syncVideos(), options);

    this.update = () => {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        const compact = this.dataset.sticky === 'true' && window.scrollY > 80;
        this.classList.toggle('rs-header--compact', compact);
        this.dataset.stickyState = compact ? 'active' : 'inactive';
        this.measure();
        this.updateRail();
      });
    };
    window.addEventListener('scroll', this.update, { ...options, passive: true });
    window.addEventListener('resize', this.update, options);
    document.addEventListener('shopify:section:select', (event) => {
      if (event.target.contains(this)) this.closeMegas();
    }, options);
    document.addEventListener('shopify:block:select', (event) => {
      if (!this.contains(event.target)) return;
      const mega = event.target.closest('.rs-mega');
      if (mega) {
        this.closeMenu(false);
        this.closeMegas(mega);
        mega.open = true;
        this.updateOpenState();
      } else if (this.drawer?.contains(event.target)) {
        this.openMenu();
        event.target.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'instant' });
      }
    }, options);
    document.addEventListener('visibilitychange', () => this.syncVideos(), options);
    this.resizeObserver = new ResizeObserver(() => { this.measure(); this.updateRail(); });
    if (this.bar) this.resizeObserver.observe(this.bar);
    if (this.rail) this.resizeObserver.observe(this.rail);
    this.videoObserver = new IntersectionObserver(() => this.syncVideos(), { threshold: .1 });
    this.querySelectorAll('.rs-nav-promo video').forEach((video) => this.videoObserver.observe(video));
    this.update();
    this.updateOpenState();
  }

  measure() {
    const rect = this.bar?.getBoundingClientRect();
    if (!rect) return;
    const bottom = Math.max(0, Math.round(rect.bottom));
    const announcementHeight = this.querySelector('.rs-nav-announcement')?.offsetHeight || 0;
    const groupHeight = Math.round(rect.height + announcementHeight);
    this.style.setProperty('--rs-nav-bottom', `${bottom}px`);
    if (!this.classList.contains('rs-header--compact') || !this.normalGroupHeight) this.normalGroupHeight = groupHeight;
    document.body.style.setProperty('--rs-nav-group-height', `${this.normalGroupHeight}px`);
    document.body.style.setProperty('--header-height', `${groupHeight}px`);
    document.body.style.setProperty('--header-group-height', `${groupHeight}px`);
    document.body.style.setProperty('--top-row-height', `${Math.round(rect.height)}px`);
    document.body.style.setProperty('--transparent-header-offset-boolean', '0');
  }

  closeMegas(except) {
    this.querySelectorAll('.rs-mega[open]').forEach((details) => { if (details !== except) details.open = false; });
    this.updateOpenState();
  }

  updateOpenState() {
    const megaOpen = !!this.querySelector('.rs-mega[open]');
    const localeOpen = !!this.querySelector('.rs-localization[open]');
    this.classList.toggle('rs-header--menu-open', this.menuOpen || megaOpen || localeOpen);
    this.classList.toggle('rs-header--drawer-open', this.menuOpen);
    if (this.backdrop) this.backdrop.hidden = !(this.menuOpen || megaOpen);
    this.measure();
    this.syncVideos();
  }

  openMenu() {
    if (!this.drawer || this.menuOpen) return;
    this.closeMegas();
    this.querySelectorAll('.rs-localization[open]').forEach((details) => details.open = false);
    this.savedFocus = document.activeElement;
    this.savedOverflow = document.documentElement.style.overflow;
    this.savedRole = this.getAttribute('role');
    this.savedModal = this.getAttribute('aria-modal');
    this.savedLabelledBy = this.getAttribute('aria-labelledby');
    this.menuOpen = true;
    this.drawer.hidden = false;
    // The persistent header, close button and panels form one modal focus boundary.
    this.setAttribute('role', 'dialog');
    this.setAttribute('aria-modal', 'true');
    this.setAttribute('aria-labelledby', this.drawer.querySelector('h2').id);
    this.setBackgroundInert(true);
    document.documentElement.style.overflow = 'hidden';
    this.trigger?.setAttribute('aria-expanded', 'true');
    this.updateOpenState();
    this.updateRail();
    this.focusable(this.primaryPane)[0]?.focus();
  }

  closeMenu(restoreFocus = true) {
    if (!this.menuOpen) return;
    this.menuOpen = false;
    this.closeBranch(false);
    this.drawer.hidden = true;
    this.querySelectorAll('.rs-localization[open]').forEach((details) => details.open = false);
    this.trigger?.setAttribute('aria-expanded', 'false');
    document.documentElement.style.overflow = this.savedOverflow ?? '';
    for (const [name, value] of [['role', this.savedRole], ['aria-modal', this.savedModal], ['aria-labelledby', this.savedLabelledBy]]) {
      if (value == null) this.removeAttribute(name); else this.setAttribute(name, value);
    }
    this.setBackgroundInert(false);
    this.updateOpenState();
    if (restoreFocus && this.isConnected) (this.savedFocus?.isConnected ? this.savedFocus : this.trigger)?.focus();
    this.savedFocus = null;
    this.savedOverflow = undefined;
  }

  selectBranch(button, moveFocus = false) {
    const panel = button ? this.querySelector(`#${CSS.escape(button.dataset.rsBranch)}`) : null;
    this.querySelectorAll('[data-rs-child]').forEach((item) => item.hidden = item !== panel);
    this.querySelectorAll('[data-rs-branch]').forEach((item) => item.setAttribute('aria-expanded', String(item === button && !!panel)));
    this.activeBranch = panel;
    this.drawer?.classList.toggle('has-child', !!panel);
    if (this.childrenPane) this.childrenPane.hidden = !panel;
    this.syncPrimaryInert();
    if (panel && moveFocus) this.focusable(panel)[0]?.focus();
  }

  closeBranch(restoreFocus) {
    const button = this.activeBranch ? this.querySelector(`[aria-controls="${CSS.escape(this.activeBranch.id)}"]`) : null;
    this.selectBranch(null);
    if (restoreFocus) button?.focus();
  }

  syncPrimaryInert() {
    if (this.primaryPane) this.primaryPane.inert = !!(this.menuOpen && this.activeBranch && this.mobile.matches);
  }

  setBackgroundInert(enabled) {
    if (enabled) {
      this.inertElements = [];
      let current = this;
      while (current.parentElement && current !== document.body) {
        for (const sibling of current.parentElement.children) {
          if (sibling !== current && !['SCRIPT', 'STYLE', 'LINK'].includes(sibling.tagName)) {
            this.inertElements.push([sibling, sibling.inert]);
            sibling.inert = true;
          }
        }
        current = current.parentElement;
      }
    } else {
      this.inertElements?.forEach(([element, wasInert]) => { element.inert = wasInert; });
      this.inertElements = [];
    }
  }

  focusable(container) {
    if (!container) return [];
    return Array.from(container.querySelectorAll('a[href],button,input,select,textarea,summary,[tabindex]')).filter((item) =>
      !item.disabled && item.tabIndex >= 0 && !item.closest('[hidden],[inert]') && item.getClientRects().length > 0 && getComputedStyle(item).visibility !== 'hidden'
    );
  }

  onKeydown(event) {
    if (event.key === 'Escape') {
      const locale = event.target.closest('.rs-localization[open]');
      if (locale) { locale.open = false; locale.querySelector('summary')?.focus(); }
      else if (this.menuOpen) {
        if (this.mobile.matches && this.activeBranch) this.closeBranch(true);
        else this.closeMenu();
      } else {
        const mega = this.querySelector('.rs-mega[open]');
        if (mega) { mega.open = false; mega.querySelector('summary')?.focus(); }
      }
      this.updateOpenState();
      event.preventDefault();
    } else if (event.key === 'ArrowLeft' && this.activeBranch?.contains(event.target)) {
      event.preventDefault();
      this.closeBranch(true);
    } else if (event.key === 'Tab' && this.menuOpen) {
      const items = this.focusable(this);
      const first = items[0], last = items.at(-1);
      if (!first) { event.preventDefault(); return; }
      if (event.shiftKey && (document.activeElement === first || !this.contains(document.activeElement))) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && (document.activeElement === last || !this.contains(document.activeElement))) { event.preventDefault(); first.focus(); }
    }
  }

  scrollRail(direction) {
    if (!this.rail) return;
    const card = this.rail.querySelector('.rs-nav-promo');
    const amount = (card?.getBoundingClientRect().width || this.rail.clientWidth * .45) + 12;
    const rtl = getComputedStyle(this.rail).direction === 'rtl' ? -1 : 1;
    this.rail.scrollBy({ left: direction * amount * rtl, behavior: this.reducedMotion.matches ? 'instant' : 'smooth' });
  }

  updateRail() {
    if (!this.rail) return;
    const max = this.rail.scrollWidth - this.rail.clientWidth;
    const offset = Math.abs(this.rail.scrollLeft);
    const previous = this.querySelector('[data-rs-rail-prev]');
    const next = this.querySelector('[data-rs-rail-next]');
    if (previous) previous.disabled = offset <= 2;
    if (next) next.disabled = max < 3 || offset >= max - 2;
  }

  syncVideos() {
    this.querySelectorAll('.rs-nav-promo video').forEach((video) => {
      const rect = video.getBoundingClientRect();
      const rail = video.closest('[data-rs-inspiration-rail]');
      const bounds = rail?.getBoundingClientRect();
      const mega = video.closest('.rs-mega');
      const inOpenSurface = mega ? mega.open : this.menuOpen;
      const visible = inOpenSurface && !document.hidden && !this.reducedMotion?.matches && !video.closest('[hidden],[inert]') && rect.width > 0 && rect.height > 0 && rect.bottom > 0 && rect.top < window.innerHeight && rect.right > (bounds?.left || 0) && rect.left < (bounds?.right || window.innerWidth);
      if (visible) {
        if (video.paused) video.play().catch(() => {});
      } else if (!video.paused) video.pause();
    });
  }

  disconnectedCallback() {
    this.closeMenu(false);
    this.controller?.abort();
    this.resizeObserver?.disconnect();
    this.videoObserver?.disconnect();
    clearTimeout(this.closeTimer);
    if (this.frame) cancelAnimationFrame(this.frame);
    this.querySelectorAll('.rs-nav-promo video').forEach((video) => video.pause());
  }
}
if (!customElements.get('rs-header')) customElements.define('rs-header', RSHeader);
